import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Icon } from '@iq/react-components';

import { getActiveComponent, getSiteComponent } from '../../../bundles/components';

const sortByName = ({ name: nameA }, { name: nameB }) => nameA.localeCompare(nameB);

const ComponentAttributesPanel = ({
  showComponentName = true,
  showCategories = true,
  categoriesFilter = [],
  showSiteIfNoActiveComponent = true,
}) => {
  const siteComponent = useSelector(getSiteComponent);
  let activeComponent = useSelector(getActiveComponent);

  if (activeComponent === undefined) {
    activeComponent = { ...siteComponent, ...(showSiteIfNoActiveComponent ? {} : { asset: {} }) };
  }

  const { name, itemDesignation, icon, asset: { variables = [] } = {} } = activeComponent;

  const filteredAttributes = useMemo(() => {
    const actualCategories = categoriesFilter.filter(Boolean);
    if (actualCategories.length === 0) {
      return variables;
    }

    return variables.filter((attribute) =>
      actualCategories.some((c) => c.toLowerCase() === attribute?.category?.toLowerCase())
    );
  }, [variables, categoriesFilter]);

  const categorizeAttributes = useCallback(() => {
    if (filteredAttributes.length === 0) {
      return {};
    }

    return filteredAttributes.reduce((acc, current) => {
      acc[current.category || 'Uncategorized'] = [
        ...(acc[current.category || 'Uncategorized'] || []),
        current,
      ];
      return acc;
    }, {});
  }, [filteredAttributes]);

  const renderWithCategories = useCallback(() => {
    const categorizedAttributes = categorizeAttributes();
    return Object.keys(categorizedAttributes)
      .toSorted((a, b) => a.localeCompare(b))
      .map((cat) => (
        <table key={cat}>
          <thead>
            <tr>
              <th>{cat}</th>
            </tr>
          </thead>
          <tbody>
            {categorizedAttributes[cat].toSorted(sortByName).map((attribute, index) => (
              <tr
                key={`${attribute.name}_${attribute.lastValueAt}_${index}`}
                className="property"
              >
                <td>{attribute.name}</td>
                <td>
                  {attribute.lastValue} {attribute.unit}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ));
  }, [categorizeAttributes, showCategories]);

  const renderWithoutCategories = useCallback(
    () => (
      <table>
        <tbody>
          {filteredAttributes.toSorted(sortByName).map((attribute, index) => (
            <tr
              key={`${attribute.name}_${attribute.lastValueAt}_${index}`}
              className="property"
            >
              <td>{attribute.name}</td>
              <td>
                {attribute.lastValue} {attribute.unit}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    ),
    [filteredAttributes]
  );

  const renderAttributes = useCallback(() => {
    if (variables.length === 0) {
      return <div className="properties empty">No attributes for selected component</div>;
    }
    if (filteredAttributes.length === 0) {
      return <div className="properties empty">No attributes for selected category filter</div>;
    }
    return (
      <div className="properties">
        {showCategories ? renderWithCategories() : renderWithoutCategories()}
      </div>
    );
  }, [variables, filteredAttributes, showCategories]);

  return (
    <div className="cp-panel-component">
      {showComponentName && (
        <div className="component-header">
          <div>
            <div className="component-name">{name}</div>
            <div className="component-id">{itemDesignation}</div>
          </div>
          <Icon icon={icon} />
        </div>
      )}
      {renderAttributes()}
    </div>
  );
};

export default ComponentAttributesPanel;
